import { prepareManualVoiceStart } from "./voice-wake-hooks";
import { create } from "zustand";
import { invoke } from "@tauri-apps/api/core";
import {
  mirroredStore,
  withMutationEmitSuppressed,
} from "@/lib/mirrored-store";
import { primeWindowOrigin } from "./use-window-origin";
import { track } from "./observability";
import { useTTS } from "./use-tts";
import {
  dictationStartFailure,
  isVoicePermissionError,
  type DictationErrorKind,
} from "./dictation-error-guidance";
import {
  dictationStartArgs,
  dictationEnginePreference,
} from "./dictation-engine-preference";
import { prewarmAgentSession } from "@/lib/agent-prewarm";
import {
  bindCommentDictationClaimToRecording,
  commentDictationClaimId,
  dropCommentDictationClaim,
} from "./comment-dictation-target";
import {
  DICTATION_ANYWHERE_OWNER_ID,
  isDictationAnywhereBusy,
} from "./dictation-anywhere/dictation-anywhere-store";
import { isDictationSettled } from "./dictation-status";
import { setVoiceIndicatorSuppressed } from "./voice-indicator-visibility";
import { isContinuousCaptureAlive } from "./continuous-voice-store";
import {
  beginVoiceStopCue,
  dropVoiceStopCue,
  playDictationStartCue,
} from "./voice-cues";
import { ensureVoicePermissionsForFeatureUse } from "./voice-permission-preflight";
import { isScreenRecordingGranted } from "./use-screenshot-capture";
import {
  activateDictationAttentionCapture,
  beginDictationAttention,
  clearDictationAttention,
} from "./dictation-attention";
import { resetDictationTranscriptDiagnostics } from "./dictation-transcript-diagnostics";
import { dictationRequest } from "./dictation-request";
import { abandonProvisionalVoiceNewChatRouting } from "./voice-new-chat-routing";
import { attachDictationVoiceToggle } from "./dictation-voice-toggle";
import { attachDictationEscapeCancel } from "./dictation-escape-cancel";
import { attachDictationNativeStateLifecycle } from "./dictation-native-state-lifecycle";
import { attachDictationFinalRouting } from "./dictation-final-routing";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { guideHoldsSends } from "./onboarding/quick-guide/guide-send-hold";
import { captureGuideShortcutGuard } from "./onboarding/quick-guide/guide-shortcut-gate";
import { debugToolsEnabled } from "@/lib/release-channel";
export { setDictationOverrideHandler } from "./dictation-override";
const dbg = (msg: string) => {
  if (!debugToolsEnabled()) return;
  void invoke("dbg_log", { msg }).catch(() => {});
};

export type DictationStatus = "idle" | "recording" | "processing" | "error";
/** Where the transcript goes: Denker (agent, composer, override handler) or
 *  dictation anywhere (clipboard and paste, owner window only). */
export type DictationMode = "denker" | "anywhere";

// isDictationSettled/dictationMicState live in "./dictation-status", not
// here: tests that mock this module wholesale need not stub them.

type State = {
  status: DictationStatus;
  /** Set with `status: "recording"` and kept until the next start. */
  mode: DictationMode;
  /** The one-shot recording's id, set with `mode`. The engine stamps it on the
   *  recording's transcripts (`session_id`), so the final routing can tell a
   *  late final of this recording from one of an earlier recording. */
  sessionId: string | null;
  /** Captured at start; late finals still belong to the guide practice. */
  guidePractice: boolean;
  lastError?: string;
  errorKind?: DictationErrorKind;
  lastFinalText?: string;
  partialText: string;
  /** Comment dictation target this recording is bound to: the composer whose
   *  mic (or open surface) claimed it. Null for a recording that goes to
   *  Denker, and once the recording settles. */
  ownerId: string | null;
  /** Composer typed in, in a focused window: dictation anywhere goes there. */
  focusedComposerId: string | null;
  unavailable: boolean;
  screenshotIntent: boolean;
  volume: number;
  deadMic: boolean;
};

type Actions = {
  start: (options?: { mode?: DictationMode }) => Promise<void>;
  stop: () => Promise<void>;
  cancel: () => Promise<void>;
  toggle: () => Promise<void>;
};

let cancelling = false;
let errorClearTimer: ReturnType<typeof setTimeout> | null = null;
let deadMicTimer: ReturnType<typeof setTimeout> | null = null;
let lastDeadMicArmAt = 0;
// Re-arming on every RMS frame is pure timer churn (clear + set per frame);
// the 3.5s deadline only needs refreshing about once a second.
const DEAD_MIC_REARM_MIN_INTERVAL_MS = 1000;
let processingWatchdog: ReturnType<typeof setTimeout> | null = null;
/** This window's recording: key press to release, 0 until known. */
const recorded = { startedAt: 0, endedAt: 0 };
const DEAD_MIC_DELAY_MS = 3500;

function clearProcessingWatchdog() {
  if (processingWatchdog) {
    clearTimeout(processingWatchdog);
    processingWatchdog = null;
  }
}

/**
 * How long to wait for a transcript before giving up on it.
 *
 * The platform recogniser has already done the recognising by the time it says
 * `stopped`; what follows is a network round-trip, and five seconds is
 * generous. The local engine does the work in that window instead — a long
 * recording on a weak machine can legitimately take tens of seconds — so
 * timing it out at five would abandon transcripts that were on their way, and
 * the user would lose what they said with no explanation.
 */
const PROCESSING_TIMEOUT_MS = 5000;
/** `lastError` when the processing watchdog gave up on the transcript. */
export const DICTATION_TRANSCRIPT_TIMEOUT = "no transcript (timeout)";
const LOCAL_PROCESSING_TIMEOUT_MS = 120000;
// Native audio stop is async: cancelling TTS resolves before the speaker tail
// (up to ~250ms acoustic epoch) has cleared the room. Opening the mic inside
// that window captures Denker's last words as user speech.
const TTS_BARGE_IN_SETTLE_MS = 200;

/** The engine the last recording started on, as `start_dictation` reports it
 *  on both platforms. With no choice stored the machine default decides, and
 *  a Whisper not yet ready falls back, so the choice alone cannot say whether
 *  the local engine is listening. */
let listeningEngine: string | null = null;

function processingTimeoutMs(): number {
  return listeningEngine === "whisper" ||
    dictationEnginePreference() === "whisper"
    ? LOCAL_PROCESSING_TIMEOUT_MS
    : PROCESSING_TIMEOUT_MS;
}

function armProcessingWatchdog() {
  clearProcessingWatchdog();
  const timeout = processingTimeoutMs();
  processingWatchdog = setTimeout(() => {
    processingWatchdog = null;
    if (useDictation.getState().status === "processing") {
      dictationRequest.cancel();
      console.warn(
        `[dictation] processing watchdog: no final event after ${timeout}ms, resetting to idle`,
      );
      useDictation.setState({
        status: "idle",
        lastError: DICTATION_TRANSCRIPT_TIMEOUT,
      });
      abandonProvisionalVoiceNewChatRouting("processing_watchdog");
      setVoiceIndicatorSuppressed(false);
    }
  }, timeout);
}

function cancelErrorClear() {
  if (errorClearTimer) clearTimeout(errorClearTimer);
  errorClearTimer = null;
}

function scheduleErrorClear() {
  cancelErrorClear();
  errorClearTimer = setTimeout(() => {
    errorClearTimer = null;
    // A retry may already have moved past this error.
    if (useDictation.getState().status !== "error") return;
    useDictation.setState({
      status: "idle",
      lastError: undefined,
      errorKind: undefined,
    });
  }, 3000);
}

/** Re-arm the patient dead-mic watchdog used during first-use startup. */
export function scheduleDeadMicCheck(): void {
  const now = Date.now();
  if (deadMicTimer && now - lastDeadMicArmAt < DEAD_MIC_REARM_MIN_INTERVAL_MS) {
    return;
  }
  if (deadMicTimer) clearTimeout(deadMicTimer);
  lastDeadMicArmAt = now;
  deadMicTimer = setTimeout(() => {
    const state = useDictation.getState();
    if (state.status === "recording" && !state.guidePractice) {
      useDictation.setState({ deadMic: true });
    }
    deadMicTimer = null;
  }, DEAD_MIC_DELAY_MS);
}

function resetVolumeAndDeadMic() {
  useDictation.setState({ volume: 0, deadMic: false });
  if (deadMicTimer) {
    clearTimeout(deadMicTimer);
    deadMicTimer = null;
  }
}

export const useDictation = create<State & Actions>()(
  mirroredStore("dictation", (set, get) => ({
    status: "idle",
    mode: "denker",
    sessionId: null,
    guidePractice: false,
    partialText: "",
    ownerId: null,
    focusedComposerId: null,
    unavailable: false,
    screenshotIntent: false,
    volume: 0,
    deadMic: false,
    start: async ({ mode = "denker" } = {}) => {
      const anywhere = mode === "anywhere";
      // Dictation anywhere stays available while the guide shows; Denker
      // voice input is held to the guide's voice lesson.
      const shortcutCurrent = anywhere
        ? () => true
        : captureGuideShortcutGuard("shortcut.voice.toggle");
      if (!shortcutCurrent()) return;
      if (isContinuousCaptureAlive()) {
        // Live listening owns the sidecar's audio engine; a one-shot start
        // would be refused natively (continuous_capture_busy) and any state
        // written here would corrupt the live-capture UI.
        dbg("dictation.start blocked: continuous voice active");
        return;
      }
      // Dictation anywhere skips what serves an agent turn (the composer
      // claim, screenshot intent, the agent prewarm) but keeps the attention
      // session, so Shift+drag marks its pasted screenshots.
      if (!anywhere && isDictationAnywhereBusy()) {
        dbg("dictation.start blocked: dictation anywhere active");
        return;
      }
      if (anywhere) dropCommentDictationClaim();
      else bindCommentDictationClaimToRecording();
      cancelErrorClear();
      if (get().unavailable) {
        dbg("dictation.start blocked: unavailable");
        return;
      }
      dictationRequest.begin();
      dropVoiceStopCue();
      cancelling = false;
      clearProcessingWatchdog();
      resetDictationTranscriptDiagnostics();
      Object.assign(recorded, { startedAt: Date.now(), endedAt: 0 });
      beginDictationAttention();
      // Anywhere draws only with Screen Recording already granted, checked
      // silently: no permission UI opens mid-dictation.
      const drawingAllowed = anywhere && isScreenRecordingGranted();
      const sessionId = crypto.randomUUID();
      set({
        status: "recording",
        mode,
        sessionId,
        guidePractice:
          !anywhere && guideHoldsSends(useOnboardingStore.getState()),
        partialText: "",
        ownerId: anywhere
          ? DICTATION_ANYWHERE_OWNER_ID
          : commentDictationClaimId(),
        lastError: undefined,
        errorKind: undefined,
        screenshotIntent: false,
        volume: 0,
        deadMic: false,
      });
      // Dictation anywhere tracks its own flow (`dictation-anywhere-analytics`).
      if (!anywhere) {
        track("desktop_voice_dictation_started");
        prewarmAgentSession("dictation_start");
      }
      dbg("dictation.start requested");
      try {
        // A user pressing the chord wants Denker silent NOW — including
        // screen-guide narration, which detector barge-in preserves. Wait
        // briefly for the native audio to actually stop so the capture does
        // not open with our own speaker tail in it.
        const tts = useTTS.getState();
        const bargeIn = tts.isActive();
        if (bargeIn) tts.cancel("explicit");
        if (bargeIn) {
          await new Promise((resolve) =>
            setTimeout(resolve, TTS_BARGE_IN_SETTLE_MS),
          );
        }
        if (!shortcutCurrent()) {
          if (get().sessionId === sessionId && get().status === "recording")
            void get().cancel();
          return;
        }
        const wakeStartToken = dictationRequest.capture();
        const handoff = prepareManualVoiceStart(wakeStartToken.isCancelled);
        if (handoff && !(await handoff)) return;
        if (get().sessionId !== sessionId || get().status !== "recording")
          return;
        if (!shortcutCurrent()) {
          void get().cancel();
          return;
        }
        // Cleared first: a stop that lands before the start answers must not
        // be timed against the previous recording's engine.
        listeningEngine = null;
        const started = await invoke<unknown>("start_dictation", {
          ...dictationStartArgs(),
          sessionId,
        });
        listeningEngine = typeof started === "string" ? started : null;
        const drawing = !anywhere || (await drawingAllowed);
        // Tell the Rust CGEventTap to start forwarding global Shift / digit /
        // Escape key presses to the webview so voice-mode shortcuts work in
        // compact overlay mode (where the window has no OS key focus). The
        // bridge filters out duplicates when the window IS focused.
        if (get().status === "recording") {
          playDictationStartCue();
          if (drawing) await activateDictationAttentionCapture();
        } else {
          void invoke("set_voice_active", { active: false }).catch(() => {});
        }
        dbg("dictation.start: invoke ok");
      } catch (e) {
        dictationRequest.cancel();
        abandonProvisionalVoiceNewChatRouting("start_failed");
        dbg(`dictation.start: invoke FAILED: ${String(e)}`);
        console.error("[dictation] start_dictation invoke failed:", e);
        if (isVoicePermissionError(undefined, String(e))) {
          void ensureVoicePermissionsForFeatureUse();
        }
        resetVolumeAndDeadMic();
        clearDictationAttention();
        void invoke("set_voice_active", { active: false }).catch(() => {});
        set(dictationStartFailure(e));
        setVoiceIndicatorSuppressed(false);
        track("desktop_voice_dictation_error", {
          error: String(e),
          phase: "start_invoke",
        });
        scheduleErrorClear();
      }
    },

    stop: async () => {
      if (isContinuousCaptureAlive()) {
        // Stopping "the mic" during live listening means stopping the
        // continuous session. A one-shot stop here would fake a "processing"
        // turn whose final never arrives (the watchdog would then surface a
        // bogus timeout) and drop the voice-active key tap mid-capture, so
        // route the intent the same way the voice-toggle handler does.
        dbg("dictation.stop routed to continuous stop");
        window.dispatchEvent(
          new Event("denker:continuous-voice-stop-requested"),
        );
        return;
      }
      recorded.endedAt = Date.now();
      const playStopCue = beginVoiceStopCue("dictation");
      dbg(
        `dictation.stop requested elapsed_ms=${recorded.startedAt ? Date.now() - recorded.startedAt : 0}`,
      );
      set({ status: "processing" });
      resetVolumeAndDeadMic();
      void invoke("set_voice_active", { active: false }).catch(() => {});
      armProcessingWatchdog();
      try {
        await invoke("stop_dictation");
        playStopCue();
      } catch (e) {
        dictationRequest.cancel();
        clearProcessingWatchdog();
        clearDictationAttention();
        set({ status: "error", lastError: String(e), errorKind: "generic" });
        setVoiceIndicatorSuppressed(false);
        track("desktop_voice_dictation_error", {
          error: String(e),
          phase: "stop_invoke",
        });
        scheduleErrorClear();
      }
    },

    cancel: async () => {
      if (isContinuousCaptureAlive()) {
        // Cancelling dictation during live listening means "stop listening"
        // (Escape's continuous handler does the same). A one-shot cancel
        // would flip the shared status and drop the voice-active key tap
        // while the native capture keeps running.
        dbg("dictation.cancel routed to continuous stop");
        window.dispatchEvent(
          new Event("denker:continuous-voice-stop-requested"),
        );
        return;
      }
      dictationRequest.cancel();
      dropVoiceStopCue();
      abandonProvisionalVoiceNewChatRouting("capture_cancelled");
      cancelling = true;
      dbg(
        `dictation.cancel requested elapsed_ms=${recorded.startedAt ? Date.now() - recorded.startedAt : 0}`,
      );
      clearProcessingWatchdog();
      set({ status: "idle", screenshotIntent: false });
      clearDictationAttention();
      setVoiceIndicatorSuppressed(false);
      resetVolumeAndDeadMic();
      void invoke("set_voice_active", { active: false }).catch(() => {});
      if (get().mode !== "anywhere") {
        track("desktop_voice_dictation_cancelled", {
          duration_ms: recorded.startedAt ? Date.now() - recorded.startedAt : 0,
        });
      }
      try {
        await invoke("stop_dictation");
      } catch {
        // Cancel is best-effort; ignore failures (sidecar may already be idle).
      }
    },

    toggle: async () => {
      if (isDictationAnywhereBusy()) return;
      const { status, start, stop } = get();
      if (status === "idle" || status === "error") return start();
      if (status === "recording") return stop();
    },
  })),
);

// The claim is local to the window that started the recording; `ownerId` is
// its mirrored copy. Each window drops it as its own status settles, so the
// reset never has to cross windows.
useDictation.subscribe((state) => {
  if (state.ownerId === null || !isDictationSettled(state.status)) return;
  withMutationEmitSuppressed(() => useDictation.setState({ ownerId: null }));
});

let listenersAttached = false;
primeWindowOrigin();

/** Wires Tauri event listeners exactly once. */
export function useDictationLifecycle(): void {
  if (listenersAttached) return;
  listenersAttached = true;

  dbg("useDictationLifecycle attaching listeners");

  attachDictationEscapeCancel(
    useDictation.getState,
    () => dictationRequest.isActive(),
    () => {
      dictationRequest.cancel();
      cancelling = true;
    },
  );

  attachDictationVoiceToggle(useDictation.getState, dbg);

  const lifecycleOptions = {
    getState: useDictation.getState,
    setState: useDictation.setState,
    setCancelling: (value: boolean) => {
      cancelling = value;
    },
    cancelRequest: () => dictationRequest.cancel(),
    clearProcessingWatchdog,
    applyMirrorVisualState: withMutationEmitSuppressed,
    resetVolumeAndDeadMic,
    scheduleErrorClear,
    debug: dbg,
  };

  attachDictationNativeStateLifecycle({
    ...lifecycleOptions,
    scheduleDeadMicCheck,
  });

  attachDictationFinalRouting({
    ...lifecycleOptions,
    isCancelling: () => cancelling,
    captureRequest: () => dictationRequest.capture(),
    recordedSpan: () => ({ ...recorded }),
  });
}
